import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errorMessage, formatPrice, Order, Product, ServiceDetail, TransferQuote } from '../api';
import { Alert, Loading, useAction, useLoad } from '../Components/ui';

const domainHint: Record<Product['category'], string> = {
    wordpress: 'The domain your WordPress site will use. If you already own it, you point it to our nameservers after purchase.',
    static: 'The domain your site will use. If you already own it, you point it to our nameservers after purchase.',
    domain: 'The domain you want to register.',
    dns: 'The domain you already own and want to host DNS for.',
    database: 'Extra databases are bought from a web package\'s Database tab.',
};

export default function Checkout() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { data: product, error: loadError, loading } = useLoad(
        () => api.get<{ product: Product }>(`/products/${slug}`).then(r => r.data.product), [slug]);
    const [domain, setDomain] = useState('');
    // Started from an owned domain ("Add web hosting"): the domain is fixed and linked to the hosting.
    const [params] = useSearchParams();
    const domainServiceId = Number(params.get('domainService')) || null;
    const { data: owned, error: ownedError } = useLoad(
        () => (domainServiceId ? api.get<ServiceDetail>(`/services/${domainServiceId}`).then(r => r.data) : Promise.resolve(null)),
        [domainServiceId]);
    useEffect(() => { if (owned?.service.domain) setDomain(owned.service.domain); }, [owned?.service.domain]);
    const [authCode, setAuthCode] = useState('');
    const [showCode, setShowCode] = useState(false);
    const [availability, setAvailability] = useState<{ domain: string; available: boolean } | null>(null);
    // Domain transfers are priced per TLD; the quote is fetched with the availability check.
    const [quote, setQuote] = useState<TransferQuote | null>(null);
    const [quoteError, setQuoteError] = useState<string | null>(null);
    const check = useAction();
    const submit = useAction();

    const checkDomain = () => check.run(async () => {
        const res = await api.get<{ domain: string; available: boolean }>('/domains/check', { params: { domain } });
        setAvailability(res.data);
        if (product?.isTransfer) {
            setQuoteError(null);
            try {
                setQuote((await api.get<{ quote: TransferQuote }>('/domains/transfer-quote', { params: { domain } })).data.quote);
            } catch (err) {
                setQuote(null);
                setQuoteError(errorMessage(err));
            }
        }
    });

    const createOrder = (e: FormEvent) => {
        e.preventDefault();
        void submit.run(async () => {
            const res = await api.post<{ order: Order }>('/orders', {
                productSlug: slug, domain, ...(product?.isTransfer ? { authCode } : {}),
                ...(domainServiceId ? { domainServiceId } : {}),
            });
            navigate(`/checkout/order/${res.data.order.id}`);
        });
    };

    if (loading) return <section className="section page-section"><Loading /></section>;
    if (loadError || !product) {
        return (
            <section className="section page-section">
                <Alert type="error">{loadError ?? 'Product not found'}</Alert>
                <Link className="btn-outline" to="/hosting">Back to hosting</Link>
            </section>
        );
    }

    const isTransfer = product.isTransfer;
    const isDomain = product.category === 'domain' && !isTransfer;
    // Registration needs a free domain; a transfer needs one that is already registered.
    const blocked = (isDomain && availability?.available === false) || (isTransfer && (availability?.available === true || !!quoteError));

    return (
        <section className="section page-section">
            <span className="section-label">Checkout</span>
            <h1 className="page-title">{product.name}</h1>
            <p className="section-desc">{product.description}</p>

            <div className="checkout-grid">
                <div className="form-card" data-aos="fade-up">
                    <h3>{isTransfer ? '1. Domain to transfer' : domainServiceId ? '1. Your domain' : '1. Choose your domain'}</h3>
                    {submit.error && <Alert type="error">{submit.error}</Alert>}
                    {ownedError && <Alert type="error">{ownedError}</Alert>}
                    {owned?.linkedHosting && (
                        <Alert type="error">
                            {owned.service.domain} already has web hosting ({owned.linkedHosting.productName}). Terminate it first to switch package.
                        </Alert>
                    )}
                    <form className="fi-form" onSubmit={createOrder}>
                        <label>
                            Domain
                            <input
                                type="text"
                                value={domain}
                                onChange={e => { setDomain(e.target.value.trim().toLowerCase()); setAvailability(null); setQuote(null); setQuoteError(null); }}
                                required
                                disabled={!!domainServiceId}
                                placeholder={product.tld ? `yourname.${product.tld}` : 'example.se'}
                            />
                            <span className="hint">
                                {domainServiceId
                                    ? 'Registered with Forget-IT — it is pointed at your new site automatically when it is ready.'
                                    : isTransfer ? 'The domain you own at another registrar.' : domainHint[product.category]}
                            </span>
                        </label>
                        {isTransfer && (
                            <label>
                                Auth code (EPP / transfer code)
                                <input
                                    type={showCode ? 'text' : 'password'}
                                    value={authCode}
                                    onChange={e => setAuthCode(e.target.value.trim())}
                                    required
                                    minLength={6}
                                    autoComplete="off"
                                    spellCheck={false}
                                    className="mono"
                                />
                                <span className="hint">
                                    Get it from your current registrar, and make sure the domain is unlocked there.{' '}
                                    <button type="button" className="link-button" onClick={() => setShowCode(v => !v)}>{showCode ? 'Hide' : 'Show'} code</button>
                                </span>
                            </label>
                        )}
                        {!domainServiceId && <div style={{ marginBottom: '1rem' }}>
                            <div className="btn-row">
                                <button type="button" className="btn-outline small" onClick={checkDomain} disabled={!domain || check.busy}>
                                    {check.busy ? 'Checking…' : 'Check availability'}
                                </button>
                            </div>
                            {check.error && <div style={{ marginTop: '0.75rem' }}><Alert type="error">{check.error}</Alert></div>}
                            {quoteError && <div style={{ marginTop: '0.75rem' }}><Alert type="error">{quoteError}</Alert></div>}
                            {availability && (
                                <div style={{ marginTop: '0.75rem' }}>
                                    {isTransfer ? (
                                        <Alert type={availability.available ? 'error' : 'success'}>
                                            {availability.available
                                                ? `${availability.domain} is not registered yet, so it cannot be transferred. Register it instead.`
                                                : quote
                                                    ? `${availability.domain} is registered and can be transferred for free (${quote.tld}, ${quote.years} year${quote.years > 1 ? 's' : ''} renewal included).`
                                                    : `${availability.domain} is registered and can be transferred with its auth code.`}
                                        </Alert>
                                    ) : isDomain ? (
                                        <Alert type={availability.available ? 'success' : 'error'}>
                                            {availability.domain} is {availability.available ? 'available!' : 'already taken.'}
                                        </Alert>
                                    ) : (
                                        <Alert type="info">
                                            {availability.available
                                                ? `${availability.domain} is not registered yet.`
                                                : `${availability.domain} is already registered. If it is yours, you point it to our nameservers after purchase.`}
                                        </Alert>
                                    )}
                                </div>
                            )}
                        </div>}
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={submit.busy || blocked || !!owned?.linkedHosting || (!!domainServiceId && !owned)}
                        >
                            {submit.busy ? 'Creating order…' : isTransfer ? 'Start transfer →' : 'Continue to payment →'}
                        </button>
                    </form>
                </div>

                <div className="form-card" data-aos="fade-up" data-aos-delay="100">
                    <h3>Order summary</h3>
                    <div className="summary-row"><span>Product</span><strong>{product.name}</strong></div>
                    <div className="summary-row"><span>Domain</span><strong>{domain || '—'}</strong></div>
                    <div className="summary-row"><span>Billing</span><strong>{product.billingPeriod === 'month' ? 'Monthly' : 'Yearly'}</strong></div>
                    {isTransfer && (
                        <div className="summary-row"><span>Includes</span><strong>
                            Transfer + {quote ? `${quote.years} year${quote.years > 1 ? 's' : ''}` : 'the'} renewal
                        </strong></div>
                    )}
                    <div className="summary-row total">
                        <span>Total</span>
                        <strong>
                            {formatPrice(quote?.priceOre ?? product.priceOre)}
                        </strong>
                    </div>
                    <p className="card-text" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                        Prices include VAT. {isTransfer
                            ? 'Domain transfers are free. The transfer starts right away and usually completes within 5 days.'
                            : 'Your service is set up automatically as soon as the payment is confirmed.'}
                    </p>
                </div>
            </div>
        </section>
    );
}
